import { TakiConfigError } from "./errors.js";

/**
 * Chargement du TAMPON DE BUILD (`/taki-build.json`) au runtime.
 *
 * Le tampon est produit a chaque build par `tools/build-stamp` (mode d'emploi et
 * forme du fichier : son README). Il dit ce qui tourne : version de plateforme,
 * commit et branche de fabrication, date, et apps embarquees avec leur version.
 * Il existe en PRODUCTION comme en dev : ce n'est pas un outil de developpement,
 * c'est ce qui permet de savoir ce qui tourne chez un client.
 *
 * Ce module vit ici, et pas dans le shell, pour une raison simple : c'est deja le
 * package qui charge du JSON au runtime, et le tampon n'a rien de temporaire. Le
 * seul consommateur d'aujourd'hui (la ligne de version du menu DEV) est, lui,
 * jetable, et il ne laisse rien derriere lui ici.
 *
 * TROIS COMPORTEMENTS, ET ILS SONT DIFFERENTS EXPRES (le patron vient de
 * `load-taki-config.ts`, la conclusion sur le repli est INVERSE) :
 *  1. Tampon ABSENT (404, reseau, adresse servie en HTML) : `found: false`, avec
 *     la raison. Ce n'est PAS une erreur fatale, et il n'y a AUCUN repli vers un
 *     second fichier : il n'existe pas de "build d'exemple", et un repli
 *     inventerait une version. Un vieux deploiement, fabrique avant ce chantier,
 *     n'a pas de tampon : l'app doit demarrer normalement sans lui.
 *  2. Tampon PRESENT mais casse (JSON invalide, forme inattendue) : on LEVE. Un
 *     fichier casse ne doit jamais etre masque, et l'appelant affiche un etat
 *     erreur honnete.
 *  3. Information manquante DANS un tampon valide (`platformVersion` a null,
 *     `git.sha` a null) : ce n'est ni une absence ni une casse, c'est un fait, et
 *     il s'affiche "inconnu". Jamais un `0.0.0` de repli.
 *
 * Aucun appelant n'a le droit de faire echouer le boot avec ce chargeur : le boot
 * est protege pour la CONFIG (un ecran d'erreur explicite), pas pour le tampon.
 */

/** Emplacement du tampon, servi a la racine du site comme la config. */
const BUILD_STAMP_URL = "/taki-build.json";

/** Une app embarquee dans le bundle, telle que sa fiche la declare. */
export interface TakiBuildStampApp {
  /** Id de l'app (dossier `src/apps/<id>`), toujours renseigne. */
  id: string;
  /** Nom affichable, ou null si la fiche ne le porte pas. */
  name: string | null;
  /** Version semver de l'app, ou null si la fiche ne la porte pas. */
  version: string | null;
  /**
   * MATURITE atteinte : `dev`, `staging` ou `production`. Ne vaut JAMAIS
   * "deprecated" : l'etat deprecated est un axe orthogonal (voir ci-dessous), et
   * l'ecraser ferait perdre la maturite atteinte avant la mise au rebut.
   */
  status: string | null;
  /** App deprecated. Axe SEPARE de `status`, jamais une valeur de `status`. */
  deprecated: boolean;
  /** Version de plateforme a laquelle le passage en deprecated a ete acte, sinon null. */
  deprecatedSince: string | null;
  /** Version de plateforme minimale exigee par l'app, sinon null. */
  minTaki: string | null;
}

/** D'ou vient l'etat git du tampon. Sert a ne jamais faire passer une devinette pour une lecture. */
export type TakiBuildStampGitSource = "git" | "ci" | "none";

/** Commit et branche de fabrication du bundle. */
export interface TakiBuildStampGit {
  /** Nom de branche, ou null quand il n'a pas pu etre lu. */
  branch: string | null;
  /** Sha court du commit, ou null quand il n'a pas pu etre lu. */
  sha: string | null;
  /** Tete detachee au moment de la fabrication (cas normal en CI). */
  detached: boolean;
  /** Modifications non commitees au moment de la fabrication ; null si inconnu. */
  dirty: boolean | null;
  source: TakiBuildStampGitSource;
}

/** Charge utile complete de `/taki-build.json`. */
export interface TakiBuildStamp {
  /** Version de la FORME du fichier. Monte si une cle change de sens. */
  schemaVersion: number;
  /** Instant de la fabrication, ISO 8601. */
  generatedAt: string;
  /** `build` = site fabrique, `serve` = servi par le serveur de dev. */
  mode: string;
  /** Version de plateforme lue dans le package.json de la racine, ou null. */
  platformVersion: string | null;
  git: TakiBuildStampGit;
  apps: TakiBuildStampApp[];
}

/**
 * Resultat d'une tentative de lecture du tampon. `found: false` = absent, cas
 * NORMAL et non fatal ; un tampon casse ne passe pas par la, il leve.
 */
export type TakiBuildStampOutcome =
  | { found: true; stamp: TakiBuildStamp }
  | { found: false; reason: string };

/* -------------------------------------------------------------------------- */
/* Lecture                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Charge le tampon de build.
 *
 * @throws {TakiConfigError} uniquement si le tampon est PRESENT et illisible
 *   (JSON invalide ou forme inattendue). Un tampon absent rend `found: false`.
 */
export async function loadTakiBuildStamp(): Promise<TakiBuildStampOutcome> {
  let response: Response;
  try {
    response = await fetch(BUILD_STAMP_URL, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
  } catch (cause) {
    return {
      found: false,
      reason: `impossible de recuperer ${BUILD_STAMP_URL} (erreur reseau : ${
        cause instanceof Error ? cause.message : String(cause)
      })`,
    };
  }

  if (!response.ok) {
    // 404 = deploiement sans tampon (fabrique avant ce chantier, ou plugin
    // retire). C'est un fait a afficher, pas une panne a signaler.
    return {
      found: false,
      reason: `echec du chargement de ${BUILD_STAMP_URL} : ${response.status} ${response.statusText}`,
    };
  }

  // CONTENT-TYPE AVANT PARSE, et ce n'est pas de la prudence decorative : sans le
  // plugin cote serveur de dev, le repli SPA de Vite repond `index.html` avec un
  // code 200 et un type `text/html`. Sans ce test, `response.json()` echouerait
  // sur du HTML avec un message incomprehensible, et le tampon passerait pour
  // casse alors qu'il est simplement absent.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return {
      found: false,
      reason: `${BUILD_STAMP_URL} n'est pas servi en JSON (content-type "${
        contentType === "" ? "absent" : contentType
      }") : le plugin tools/build-stamp n'est probablement pas branche.`,
    };
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch (cause) {
    throw new TakiConfigError(
      `Le contenu de ${BUILD_STAMP_URL} n'est pas un JSON valide.`,
      { cause },
    );
  }

  return { found: true, stamp: parseTakiBuildStamp(raw) };
}

/* -------------------------------------------------------------------------- */
/* Validation structurelle (sans dependance externe)                           */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Chaine non vide, ou null. Un champ absent et un champ vide se valent ici. */
function optionalString(value: unknown, path: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") {
    throw new TakiConfigError(
      `Champ "${path}" invalide dans taki-build.json (chaine ou null attendu).`,
    );
  }
  return value.length === 0 ? null : value;
}

function requireString(
  source: Record<string, unknown>,
  key: string,
  path: string,
): string {
  const value = source[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new TakiConfigError(
      `Champ "${path}" manquant ou invalide dans taki-build.json (chaine non vide attendue).`,
    );
  }
  return value;
}

/** Booleen tolerant : absent vaut `false`, toute autre forme leve. */
function optionalBoolean(value: unknown, path: string): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value !== "boolean") {
    throw new TakiConfigError(
      `Champ "${path}" invalide dans taki-build.json (booleen attendu).`,
    );
  }
  return value;
}

/** Booleen a trois etats : absent vaut `null` (inconnu), pas `false`. */
function tristateBoolean(value: unknown, path: string): boolean | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "boolean") {
    throw new TakiConfigError(
      `Champ "${path}" invalide dans taki-build.json (booleen ou null attendu).`,
    );
  }
  return value;
}

/**
 * Une valeur inconnue est ramenee a `none` plutot que de lever : c'est le champ
 * le moins structurant du tampon, et un tampon plus recent que le lecteur (une
 * quatrieme provenance un jour) ne doit pas casser une ligne de version.
 */
function parseGitSource(value: unknown): TakiBuildStampGitSource {
  return value === "git" || value === "ci" ? value : "none";
}

function parseGit(raw: unknown): TakiBuildStampGit {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "git" manquant ou invalide dans taki-build.json (objet attendu).`,
    );
  }
  return {
    branch: optionalString(raw["branch"], "git.branch"),
    sha: optionalString(raw["sha"], "git.sha"),
    detached: optionalBoolean(raw["detached"], "git.detached"),
    dirty: tristateBoolean(raw["dirty"], "git.dirty"),
    source: parseGitSource(raw["source"]),
  };
}

function parseApp(raw: unknown, index: number): TakiBuildStampApp {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Element "apps[${index}]" invalide dans taki-build.json (objet attendu).`,
    );
  }
  const path = `apps[${index}]`;
  const deprecatedSince = optionalString(
    raw["deprecatedSince"],
    `${path}.deprecatedSince`,
  );
  return {
    id: requireString(raw, "id", `${path}.id`),
    name: optionalString(raw["name"], `${path}.name`),
    version: optionalString(raw["version"], `${path}.version`),
    status: optionalString(raw["status"], `${path}.status`),
    // Un `deprecatedSince` rempli suffit : les deux formes du meme fait sont
    // acceptees, l'etat deprecated ne peut pas se perdre pour un booleen oublie.
    deprecated:
      optionalBoolean(raw["deprecated"], `${path}.deprecated`) ||
      deprecatedSince !== null,
    deprecatedSince,
    minTaki: optionalString(raw["minTaki"], `${path}.minTaki`),
  };
}

/**
 * Valide une valeur inconnue (issue du JSON) et la renvoie typee.
 * Exportee pour valider une charge injectee sans `fetch` (tests).
 *
 * @throws {TakiConfigError} si la structure ne respecte pas le contrat.
 */
export function parseTakiBuildStamp(raw: unknown): TakiBuildStamp {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `taki-build.json doit contenir un objet JSON a la racine.`,
    );
  }

  const schemaVersion = raw["schemaVersion"];
  if (typeof schemaVersion !== "number" || !Number.isFinite(schemaVersion)) {
    throw new TakiConfigError(
      `Champ "schemaVersion" manquant ou invalide dans taki-build.json (nombre attendu).`,
    );
  }

  const apps = raw["apps"];
  if (!Array.isArray(apps)) {
    throw new TakiConfigError(
      `Champ "apps" manquant ou invalide dans taki-build.json (tableau attendu).`,
    );
  }

  return {
    schemaVersion,
    generatedAt: requireString(raw, "generatedAt", "generatedAt"),
    mode: requireString(raw, "mode", "mode"),
    platformVersion: optionalString(raw["platformVersion"], "platformVersion"),
    git: parseGit(raw["git"]),
    apps: apps.map(parseApp),
  };
}
